import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Card, CardBody, DataTable, EmptyState, ErrorState, Field, LoadingState, TextInput } from "@odingard/design-system";
import type { AdminApplicationSummary, AdminAuditEvent, AdminBundleSummary, CatalogLifecycleStatus } from "@odingard/sdk-client";
import { ApiError } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

type Tab = "applications" | "bundles" | "audit";

function statusTone(status: CatalogLifecycleStatus): "success" | "info" | "warning" | "neutral" | "danger" {
  if (status === "available") return "success";
  if (status === "maintenance" || status === "deprecated") return "warning";
  if (status === "suspended" || status === "retired") return "danger";
  if (status === "coming_soon" || status === "beta" || status === "preview") return "info";
  return "neutral";
}

export function CatalogAdmin() {
  const [tab, setTab] = useState<Tab>("applications");
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Catalog administration</h1>
          <p>Govern the global Studio application catalog. Changes are audited and for platform staff only.</p>
        </div>
        <Link to="/studio/catalog-admin/apps/new">
          <Button size="sm">New application</Button>
        </Link>
      </div>

      <div className="ods-row" role="tablist" style={{ gap: 8, marginBottom: 16 }}>
        {(["applications", "bundles", "audit"] as Tab[]).map((t) => (
          <Button key={t} size="sm" variant={tab === t ? "primary" : "ghost"} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>
            {t === "applications" ? "Applications" : t === "bundles" ? "Bundles" : "Audit trail"}
          </Button>
        ))}
      </div>

      {tab === "applications" && <ApplicationsTab />}
      {tab === "bundles" && <BundlesTab />}
      {tab === "audit" && <AuditTab />}
    </>
  );
}

function ApplicationsTab() {
  const navigate = useNavigate();
  const state = useAsync(async () => (await api.adminApplications()).applications, []);
  if (state.loading) return <LoadingState label="Loading applications…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={state.reload} />;
  const rows = state.data ?? [];
  if (rows.length === 0) return <EmptyState title="No applications" description="Create the first catalog application." />;
  return (
    <Card>
      <CardBody>
        <DataTable<AdminApplicationSummary>
          caption="Catalog applications"
          onRowClick={(r) => navigate(`/studio/catalog-admin/apps/${r.slug}`)}
          columns={[
            { key: "name", header: "Application", render: (r) => <span>{r.icon} {r.name}</span> },
            { key: "slug", header: "Slug", render: (r) => <code>{r.slug}</code> },
            { key: "status", header: "Lifecycle", render: (r) => <Badge tone={statusTone(r.lifecycleStatus)}>{r.lifecycleStatus}</Badge> },
            { key: "category", header: "Category", render: (r) => r.categoryName ?? "None" },
            { key: "order", header: "Order", render: (r) => String(r.sortOrder) },
          ]}
          rows={rows}
        />
      </CardBody>
    </Card>
  );
}

function BundlesTab() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const state = useAsync(async () => (await api.adminBundles()).bundles, []);

  async function create() {
    setError(null);
    try {
      await api.createAdminBundle({ slug: slug.trim(), name: name.trim() });
      navigate(`/studio/catalog-admin/bundles/${slug.trim()}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to create bundle.");
    }
  }

  if (state.loading) return <LoadingState label="Loading bundles…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={state.reload} />;
  const rows = state.data ?? [];
  return (
    <>
      <div className="ods-row" style={{ justifyContent: "flex-end", marginBottom: 12 }}>
        <Button size="sm" variant="secondary" onClick={() => setCreating((v) => !v)}>{creating ? "Cancel" : "New bundle"}</Button>
      </div>
      {creating && (
        <Card>
          <CardBody>
            <div className="ods-form-grid">
              <Field label="Slug">{(id) => <TextInput id={id} value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="bundle-two" />}</Field>
              <Field label="Name">{(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="New Bundle" />}</Field>
            </div>
            {error && <p className="ods-error-text" role="alert">{error}</p>}
            <Button size="sm" onClick={create} disabled={!slug.trim() || !name.trim()}>Create</Button>
          </CardBody>
        </Card>
      )}
      {rows.length === 0 ? (
        <EmptyState title="No bundles" description="Create a bundle to package applications together." />
      ) : (
        <Card>
          <CardBody>
            <DataTable<AdminBundleSummary>
              caption="Catalog bundles"
              onRowClick={(r) => navigate(`/studio/catalog-admin/bundles/${r.slug}`)}
              columns={[
                { key: "name", header: "Bundle", render: (r) => r.name },
                { key: "slug", header: "Slug", render: (r) => <code>{r.slug}</code> },
                { key: "kind", header: "Kind", render: (r) => r.kind },
                { key: "status", header: "Lifecycle", render: (r) => <Badge tone={statusTone(r.lifecycleStatus)}>{r.lifecycleStatus}</Badge> },
              ]}
              rows={rows}
            />
          </CardBody>
        </Card>
      )}
    </>
  );
}

function AuditTab() {
  const state = useAsync(async () => (await api.adminAudit(100)).events, []);
  if (state.loading) return <LoadingState label="Loading audit trail…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={state.reload} />;
  const rows = state.data ?? [];
  if (rows.length === 0) return <EmptyState title="No catalog activity yet" description="Catalog mutations will appear here." />;
  return (
    <Card>
      <CardBody>
        <DataTable<AdminAuditEvent>
          caption="Catalog audit trail"
          columns={[
            { key: "when", header: "When", render: (r) => new Date(r.createdAt).toLocaleString() },
            { key: "action", header: "Action", render: (r) => <code>{r.action}</code> },
            { key: "target", header: "Target", render: (r) => r.target ?? "None" },
            { key: "actor", header: "Actor", render: (r) => r.actorEmail ?? r.actorUserId ?? "None" },
          ]}
          rows={rows}
        />
      </CardBody>
    </Card>
  );
}
